import {
  Button,
  CIActionGuard,
  CIPageHeader,
  CIStatusCapsule,
  MyIcon,
  useUserPermission
} from 'ccs-ui-components';
import { useTranslation } from 'react-i18next';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import BackIcon from '../../../../_components/icons/BackIcon';
import RejectIcon from '../../../../_components/icons/RejectIcon';
import { EVENT_MODES, STATUS_TYPES } from '../../../../constants';
import { IEvent, IEventStatusHistory } from '../../../../models/events/IEvent';

import DropdownMenuButton from '../../../../../common/components/UI/menu/DropdownMenuButton';
import { REGISTRY_TYPE } from '../../../../../dashboard/constants/CommonConstants';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { fetchEventRequest } from '../../../../store/actions/sagaActions/events/EventNameSagaActions';
import classes from '../index.module.scss';

interface ITitleBody {
  isSticky: boolean;
  preview: boolean;
  eventDetails: IEvent[];
  getClassNameForStatus: (
    status: string,
    primaryClassName: string,
    classes: {
      readonly [key: string]: string;
    }
  ) => string;
  handleViewCancelClick: () => void;
  navigateToEditEvent: () => void;
  setModalShow: (value: boolean) => void;
  handlePatchEvent: (updateStatus: string, authComm: string) => void;
  setApproveModalShow: (value: boolean) => void;
  setShowCommentDialog: (value: boolean) => void;
  setEditModalShow: (value: boolean) => void;
  mode: string | undefined;
  eventDetailsForHistory: IEventStatusHistory[] | undefined;
  handleReturnButton: () => void;
  onDependencyGraphFound: (isDependencyGraphFound: boolean, registryName: string, entityVersion: string, entityStatus: string) => void
}

const TitleBody = ({
  isSticky,
  preview,
  eventDetails,
  getClassNameForStatus,
  handleViewCancelClick,
  navigateToEditEvent,
  setModalShow,
  handlePatchEvent,
  setApproveModalShow,
  setShowCommentDialog,
  setEditModalShow,
  mode,
  eventDetailsForHistory,
  handleReturnButton,
  onDependencyGraphFound
}: ITitleBody) => {
  const { t: translate } = useTranslation();
  const { permissionObject } = useUserPermission();
  const { isLoading } = useSelector((state) => state.getEventIdReducer);
  const { eventname, version } = useParams<{
    eventname: string;
    status: string;
    mode: string;
    version: string;
  }>();
  const dispatch = useDispatch<AppDispatch>();
  const { eventName, status, version: eventVersion } = eventDetails[0];

  const onDeleteSuccess = () => {
    dispatch(fetchEventRequest(`${eventname}`, parseInt(`${version}`, 10)));
  };

  const getDropDownMenuButton = () => {
    if (mode === EVENT_MODES.VIEW) {
      return (
        <DropdownMenuButton
          registryName={eventName}
          status={status}
          deleteURL={`events/${eventName}/version/${eventVersion}`}
          onDeleteSuccess={onDeleteSuccess}
          entityData={eventDetails[0]}
          registryType={REGISTRY_TYPE.EVENTS}
          onDependencyGraphFound={onDependencyGraphFound}
        />
      );
    }
    return null;
  };

  return (
    <div className={classes.title}>
      <div className={classes['title-wrapper']}>
        <div className={classes['title-name']} id="title">
          <CIPageHeader
            header={!isLoading ? `${eventName}` : ''}
            className={isSticky ? classes['sticky-header-header'] : ''}
          />
          <p
            id="title-body"
            className={`${classes['version-text']} ${!isLoading ? classes['text-style'] : ''
              }`}
          >
            {' '}
            {`Ver ${eventVersion}`}
          </p>
        </div>
        {status && (
          <div
            className={getClassNameForStatus(
              status ?? '',
              classes.statusheader,
              classes
            )}
          >
            <CIStatusCapsule status={status} />
          </div>
        )}
      </div>
      <div className={`${classes['btn-edit-space']} ci-button-group`}>
        {preview && (
          <div className={classes['header-btn']}>
            {status?.toLocaleUpperCase()
              === STATUS_TYPES.DRAFT && (
                <>
                  <Button
                    id="titleBody"
                    variant="secondary"
                    onClick={handleViewCancelClick}
                    data-testid="btn-cancel"
                    className={classes['back-btn']}
                  >
                    {translate('events_wrapper.lbl_back_btn')}
                  </Button>
                  {CIActionGuard(
                    [
                      requiredAuthPermission.Events.edit,
                      requiredAuthPermission.Events.approve
                    ],
                    permissionObject.permission
                  )
                    && (
                      <>
                        <Button
                          variant="primary"
                          onClick={navigateToEditEvent}
                          data-testid="btn-edit-draft"
                          className={classes['back-btn']}
                        >
                          {translate('events_wrapper.lbl_edit')}
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => setModalShow(true)}
                          data-testid="btn-submit"
                          className={classes['back-btn']}
                        >
                          {translate('events_wrapper.lbl_submit_for_review')}
                        </Button>
                      </>
                    )}
                  {getDropDownMenuButton()}
                </>
              )}

            {status?.toLocaleUpperCase()
              === STATUS_TYPES.PENDING_APPROVAL && (
                <>
                  <>
                    <Button
                      id="pendingcancel"
                      variant="secondary"
                      onClick={handleViewCancelClick}
                      data-testid="btn-cancel"
                      className={classes['back-btn']}
                    >
                      {translate('events_wrapper.lbl_back_btn')}
                    </Button>
                    {CIActionGuard(
                      [
                        requiredAuthPermission.Events.edit,
                        requiredAuthPermission.Events.approve
                      ],
                      permissionObject.permission
                    )
                      && (
                        <Button
                          variant="primary"
                          onClick={() =>
                            handlePatchEvent(STATUS_TYPES.REVIEW_CANCEL, '')}
                          data-testid="btn-move-to-draft"
                          className={classes['back-btn']}
                        >
                          {translate('events_wrapper.lbl_cancel_review')}
                        </Button>
                      )}
                  </>
                  {CIActionGuard(
                    [requiredAuthPermission.Events.approve],
                    permissionObject.permission
                  )
                    && (
                      <>
                        <Button
                          variant="primary"
                          onClick={() => setApproveModalShow(true)}
                          data-testid="btn-approve"
                          className={classes['back-btn']}
                        >
                          <MyIcon name="largeCheckIcon" />
                          {translate('events_wrapper.lbl_approve')}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => setShowCommentDialog(true)}
                          data-testid="btn-reject"
                          className={classes['btn-reject']}
                        >
                          <BackIcon /> {'    '}{' '}
                          {translate('events_wrapper.lbl_reject')}
                        </Button>
                      </>
                    )}
                  {getDropDownMenuButton()}
                </>
              )}

            {status?.toLocaleUpperCase()
              === STATUS_TYPES.ACTIVE && (
                <>
                  <Button
                    id="btn1"
                    variant="secondary"
                    onClick={handleViewCancelClick}
                    data-testid="btn-cancel"
                    className={classes['back-btn']}
                  >
                    {mode === EVENT_MODES.VIEW
                      ? `${translate('events_wrapper.lbl_back_btn')}`
                      : `${translate('events_wrapper.lbl_cancel_btn')}`}
                  </Button>
                  {CIActionGuard(
                    [
                      requiredAuthPermission.Events.edit,
                      requiredAuthPermission.Events.approve
                    ],
                    permissionObject.permission
                  )
                    && (
                      <Button
                        variant="primary"
                        onClick={() => setEditModalShow(true)}
                        data-testid="btn-edit"
                        className={classes['back-btn']}
                      >
                        {translate('events_wrapper.lbl_edit')}
                      </Button>
                    )}
                  {getDropDownMenuButton()}
                </>
              )}
            {status?.toLocaleUpperCase()
              === STATUS_TYPES.RETURNED && (
                <>
                  {eventDetailsForHistory?.length !== 0 && (
                    <>
                      <Button
                        className={classes['btn-reject-with-icon']}
                        variant="secondary"
                        onClick={handleReturnButton}
                        data-testid="btn-wrapper-cancel"
                      >
                        <RejectIcon />
                      </Button>
                      <Button
                        id="btn2"
                        variant="secondary"
                        onClick={handleViewCancelClick}
                        data-testid="btn-cancel"
                        className={classes['edit-btn']}
                      >
                        {mode === EVENT_MODES.VIEW
                          ? `${translate('events_wrapper.lbl_back_btn')}`
                          : `${translate('events_wrapper.lbl_cancel_btn')}`}
                      </Button>
                    </>
                  )}
                  {CIActionGuard(
                    [
                      requiredAuthPermission.Events.edit,
                      requiredAuthPermission.Events.approve
                    ],
                    permissionObject.permission
                  )
                    && (
                      <Button
                        variant="primary"
                        onClick={navigateToEditEvent}
                        data-testid="btn-edit-returned"
                        className={classes['back-btn']}
                      >
                        {translate('events_wrapper.lbl_edit')}
                      </Button>
                    )}
                  {getDropDownMenuButton()}
                </>
              )}
            {status?.toLocaleUpperCase()
              === STATUS_TYPES.INACTIVE
              && eventDetailsForHistory?.length !== 0 && (
                <>
                  <Button
                    id="btn2"
                    variant="secondary"
                    onClick={handleViewCancelClick}
                    data-testid="btn-cancel"
                    className={classes['edit-btn']}
                  >
                    {mode === EVENT_MODES.VIEW
                      ? `${translate('events_wrapper.lbl_back_btn')}`
                      : `${translate('events_wrapper.lbl_cancel_btn')}`}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() =>
                      handlePatchEvent(STATUS_TYPES.DRAFT, '')}
                    data-testid="btn-move-to-draft"
                    className={classes['back-btn']}
                  >
                    {translate('events_wrapper.lbl_cancel_review')}
                  </Button>
                  {getDropDownMenuButton()}
                </>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TitleBody;
